import { Link } from '@atoms/*';
import { BINARY_OPTIONS } from '@shared/constants';
import { TEXT_AREA_MAX_LENGTH } from '@constants';

export const beforeYouStartContent = {
  title: 'Before you start',
  needToKnowItems: {
    sectionTitle: 'To help us resolve the issue, we need to know:',
    itemList: ['the location', 'the details of the issue'],
  },
  usefulToInclude: {
    sectionTitle: 'It’s useful to provide:',
    itemList: ['photos of the issue', 'your contact details'],
  },
  linkObjectArrays: [
    {
      level: 1,
      title: 'Home',
      href: 'https://www.westminster.gov.uk/',
    },
    {
      level: 2,
      title: 'Report it',
      href: 'https://www.westminster.gov.uk/report-it',
    },
    {
      level: 3,
      title: `Report an animal or dog nuisance`,
    },
  ],
  infoBox: {
    description: (
      <div aria-label="Link to noise form">
        <p className="mb-3">
          If a dog is barking at night or on the weekend, use our noise form to
          report it for a faster response.
        </p>
        <Link
          href="https://mywestminster.westminster.gov.uk/report-it/noise"
          linkType="inline"
          className="text-text-dark mt-3"
          aria-label="Information box"
          external
        >
          Report dog barking at night or weekends.
        </Link>
      </div>
    ),
  },
};

export const reportTypeQuestionData = {
  title: 'Are you reporting a stray dog?',
  key: 'isStrayDog',
  description: `A dog might be a stray if it doesn’t have an owner, isn’t wearing identification tags, and is roaming in places it shouldn’t be.`,
  options: BINARY_OPTIONS,
};

export const issueTypes = [
  {
    id: '1',
    name: 'AggressiveDog',
    label: 'Aggressive dog incident',
    checked: false,
  },
  {
    id: '2',
    name: 'AnimalWelfare',
    label: 'Animal welfare',
    checked: false,
  },
  {
    id: '3',
    name: 'BarkingDog',
    label: 'Barking dog',
    checked: false,
  },
  {
    id: '4',
    name: 'LostAnimal',
    label: 'Lost animal',
    checked: false,
  },
  {
    id: '5',
    name: 'Wildlife',
    label: 'Wildlife (for example overfeeding or injured animals)',
    checked: false,
  },
  {
    id: '6',
    name: 'SomethingElse',
    label: "It's something else",
    checked: false,
  },
];

export const issueTypeInfo = {
  AggressiveDog:
    'Dogs that are dangerously out of control in a public place should be reported to the police by calling 101. Where there is a risk to public safety, call 999.',
  AnimalWelfare:
    'Emergency animal welfare concerns can be reported to the RSPCA on 0300 1234 999.',
  BarkingDog: (
    <>
      {`If a dog is barking at night or on the weekend, use our noise form to report it for a faster response.`}
      <Link
        href="https://mywestminster.westminster.gov.uk/report-it/noise"
        linkType="inline"
        className="text-text-dark mt-3 block"
      >
        Report dog barking at night or weekends.
      </Link>
    </>
  ),
};

export const canBeContactedQuestionData = {
  title: 'Can we contact you about your issue?',
  description: (
    <div className="text-bodyLg flex flex-col gap-3">
      <p>
        By providing your details, you agree that we may contact you by email if
        more information is needed.
      </p>
      <p>
        Your details will be confidential. Only the council will see the
        information you share.
      </p>
    </div>
  ),
  key: 'canBeContacted',
  options: BINARY_OPTIONS,
};

export const isRecurringIssueQuestionData = {
  title: 'Is it a recurring issue?',
  key: 'isRecurringIssue',
  radioOptions: [
    {
      title: 'Has the issue happened before?',
      errorPath: 'isRecurringIssue',
      options: [
        {
          id: 'yes',
          name: 'yes',
          labelText: 'Yes',
        },
        {
          id: 'no',
          name: 'no',
          labelText: 'No',
        },
      ],
    },
  ],
  textArea: {
    title: 'When has it previously happened?',
    description:
      'Please provide as much detail as possible, including who was involved, when it happened and where.',
    errorPath: 'whenDidItHappen',
    id: 'whenDidItHappen',
    maxLimit: TEXT_AREA_MAX_LENGTH,
  },
};

export const issueInfoQuestionData = {
  titleMandatory: 'Describe the issue',
  titleOptional: 'Describe the issue (optional)',
  key: 'issueInfo',
  description: 'Add any more information that may be useful to know.',
  errorMessage: 'Enter details about the issue',
};

export const emailConfirmationQuestionData = {
  title: 'Do you want a confirmation email of your report?',
  key: 'emailConfirmation',
  options: BINARY_OPTIONS,
};

export const additionalLocationDetails = {
  id: 'additionalLocationDetails',
  errorPath: 'additionalLocationDetails',
  labelText: 'Please provide more information about the location (optional)',
  description:
    'If you are unsure of the exact address, provide as much detail as you can, for example if it is close to a shop, a road junction or other landmark.',
  maxLength: TEXT_AREA_MAX_LENGTH,
};

export const locationContentData = {
  titleWithLocation: 'Check if the location is correct',
  titleWithoutLocation: 'Location of the issue',
  descriptionWithLocation: (
    <div className="text-bodyLg flex flex-col gap-4">
      <p>We used your photo to find where the issue is located.</p>
      <p>You can change where the issue is located using the map.</p>
    </div>
  ),
  descriptionWithoutLocation: (
    <p className="text-bodyXl">Please select a location on the map.</p>
  ),
  addressTitleWithLocation: 'Issue Location',
  addressTitleWithoutLocation: 'You have selected',
};
